import React, { useContext, useMemo } from "react";

import {
  AuthContext,
  JobsContext,
  MatchContext,
  ModalContext,
  SearchContext,
} from "context/providers";

import {
  CustomLinkModal,
  NoteModal,
  UserEditProfileModal,
  MatchModal,
  FinalistModal,
  AssessmentModal,
  CalendarModal,
  JobModal,
} from "./modals";

const MODALS_BY_TYPE = {
  assessment: AssessmentModal,
  userEditProfile: UserEditProfileModal,
  link: CustomLinkModal,
  note: NoteModal,
  match: MatchModal,
  finalist: FinalistModal,
  calendar: CalendarModal,
  jobModal: JobModal,
};

const TEXT_CONTENT_LINKING = {
  user: {
    editorLabel: "Create your user note",
    editorSubLabel:
      "Remember: These notes are associated with the user profile",
  },
  match: {
    editorLabel: "Create your match note",
    editorSubLabel:
      "Remember: These notes are associated with the match record",
  },
};

const DummyModal = () => <></>;

const ModalContainer = () => {
  const { activeModal, hideModal } = useContext(ModalContext);
  const { matches, ideal } = useContext(MatchContext);
  const { hits } = useContext(SearchContext);
  const { longList } = useContext(JobsContext);
  const { user: loggedInUser } = useContext(AuthContext);

  const { user, ...rest } = useMemo(() => {
    if (activeModal?.userId) {
      const user = [
        ...hits,
        ...ideal,
        ...matches.map((m) => m.user),
        ...longList.items.map((e) => e.user),
      ].find((hit) => hit?.id === activeModal.userId);

      return {
        user,
        ...TEXT_CONTENT_LINKING.user,
        ...activeModal,
      };
    }

    if (activeModal?.applicationId) {
      const user = matches.find(
        (hit) => hit.applicationId === activeModal.applicationId
      );

      const labels = TEXT_CONTENT_LINKING.match;

      return { user, ...labels, ...activeModal };
    }

    return { user: null, ...activeModal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal]);

  const modalTarget = useMemo(() => {
    if (activeModal?.applicationId) {
      return "Match";
    }

    if (activeModal?.userId) {
      return "User";
    }
  }, [activeModal]);

  const ModalComponent = useMemo(() => {
    if (!activeModal) {
      return DummyModal;
    }

    // Sending assessments is restricted to a cognito group. Enforced here so
    // the gate holds no matter which action surface opened the modal.
    if (
      activeModal.type === "assessment" &&
      !loggedInUser?.canSendAssessments
    ) {
      return DummyModal;
    }

    return MODALS_BY_TYPE[activeModal.type] || DummyModal;
  }, [activeModal, loggedInUser]);

  return (
    <ModalComponent
      user={user}
      modalTarget={modalTarget}
      onClose={hideModal}
      {...rest}
    />
  );
};

export default ModalContainer;
